import {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { removeUnitOfMeasureDelimiters } from 'components/WorkspaceEditor/components/NodeContents/hooks/useFormatReplacementOption'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import {
    PROPERTY_EDITOR_PILL_STATE_VARIANT as PillStateVariants,
    PILL_TRACK_CHANGE_STATE as PillTrackChangeState,
} from '../constants'

/**
 * Formats replacement text by removing UOM delimiters with error handling.
 *
 * This function provides a safe way to process text that may contain UOM delimiters,
 * similar to the formatting logic used in useFormatReplacementOption.ts.
 *
 * @param text - Raw text that may contain UOM delimiters
 * @returns Formatted text with delimiters removed and whitespace trimmed
 *
 * @example
 * formatResetText("  |START_IP|6 inches|END_IP||START_SI|15 cm|END_SI|  ")
 * // Returns: "6 inches15 cm"
 */
export const formatResetText = (text: string | undefined): string => {
    if (!text) return ''

    try {
        const trimmedText = text.trim()
        return removeUnitOfMeasureDelimiters(trimmedText)
    } catch (error) {
        console.warn('Failed to format reset text:', error)
        // Fallback: return trimmed text without UOM processing
        return text.trim()
    }
}

/**
 * Type guard to check if an element is a ReplacementOption
 */
const isReplacementOption = (
    element: ChildTextContentElement | TextContentElement
): boolean => {
    return element?.type === NODE_TYPES.ReplacementOption
}

/**
 * Type guard to check if an element is a SelectionOption
 */
const isSelectionOption = (element: ChildTextContentElement): boolean => {
    return element.type === NODE_TYPES.SelectionOption
}

/**
 * Determines the appropriate text content for reset operations.
 *
 * This function handles the logic of selecting between element text and placeholderText
 * based on the element type, reset operation type, and whether the element contains UOM children.
 *
 * @param element - The element being processed
 * @param isReplacementOption - Whether the element is a ReplacementOption
 * @param resetOptionType - The type of reset operation ('RESET' or 'DEACTIVATE')
 * @returns The appropriate text content to use for the reset
 */
const determineResetText = (
    element: Partial<ChildTextContentElement>,
    isReplacementOption: boolean,
    resetOptionType: 'RESET' | 'DEACTIVATE'
): string | undefined => {
    // For RESET operations on ReplacementOptions, always use placeholderText
    if (isReplacementOption && resetOptionType === 'RESET') {
        return element.placeholderText
    }

    // Check if this is a ReplacementOption with UOM children but empty text
    // In this case, use placeholderText as it likely contains the UOM delimited content
    const hasUOMChildrenWithEmptyText =
        isReplacementOption &&
        element.elements?.length !== 0 &&
        (!element.text || element.text.trim().length === 0)

    if (hasUOMChildrenWithEmptyText) {
        return element.placeholderText
    }

    // For all other cases, use the current text
    return element.text
}

/**
 * Processes a single element during reset operations with enhanced UOM delimiter handling.
 *
 * This function handles resetting pill elements (ReplacementOption and SelectionOption) to their
 * appropriate states while properly formatting text content that may contain Units of Measure delimiters.
 *
 * @param element - Element to process
 * @param resetOptionType - Type of reset operation to perform
 * @returns Processed element with appropriate reset state and formatted text
 *
 * @example
 * // RESET operation on ReplacementOption with UOM delimiters
 * const element = {
 *   type: 'ReplacementOption',
 *   text: '|START_IP|12 inches|END_IP|',
 *   placeholderText: '|START_IP|6 inches|END_IP|',
 *   optionItemState: 'OptedIn'
 * }
 * const result = processResetElement(element, 'RESET')
 * // Returns: { ...element, text: '6 inches', optionItemState: null, changeType: 'RESETOPTION' }
 */
export const processResetElement = (
    element: Partial<ChildTextContentElement>,
    resetOptionType?: 'RESET' | 'DEACTIVATE'
): Partial<ChildTextContentElement> => {
    // Early validation - return unchanged if invalid inputs
    if (!element || typeof element !== 'object' || !resetOptionType) {
        return element
    }

    // Determine element types
    const isReplacementOptionItem = isReplacementOption(
        element as TextContentElement
    )
    const isSelectionOptionItem = isSelectionOption(
        element as ChildTextContentElement
    )
    const isPillVariant = isReplacementOptionItem || isSelectionOptionItem

    // Skip processing for non-pill elements
    if (!isPillVariant) {
        return element
    }

    // Determine the appropriate text content for the reset operation
    const rawResetText = determineResetText(
        element,
        isReplacementOptionItem,
        resetOptionType
    )

    // Apply UOM delimiter formatting for ReplacementOptions
    const resetText = isReplacementOptionItem
        ? formatResetText(rawResetText)
        : rawResetText

    // Apply reset state changes based on operation type
    const baseResetElement = {
        ...element,
        text: resetText,
    }

    if (resetOptionType === 'RESET') {
        return {
            ...baseResetElement,
            changeType: PillTrackChangeState.ResetOption as 'MODIFIED',
            optionItemState: null, // Clear state completely for reset
        }
    }

    if (resetOptionType === 'DEACTIVATE') {
        return {
            ...baseResetElement,
            changeType: PillTrackChangeState.Disabled as 'MODIFIED',
            optionItemState: PillStateVariants.InActive, // Set to InActive for deactivation
        }
    }

    return element
}

/**
 * Handles reset operations for option groups with improved element processing
 *
 * @param elements - Array of text content elements to process
 * @param resetOptionType - Type of reset operation ('RESET' or 'DEACTIVATE')
 * @returns Updated array of elements with reset states applied
 */
export const handleOptionGroupReset = (
    elements: TextContentElement[],
    resetOptionType?: 'RESET' | 'DEACTIVATE'
): TextContentElement[] => {
    try {
        const updatedElements = elements.map((element) => {
            if (element.type === NODE_TYPES.OptionGroup) {
                if (!element.elements || element.elements?.length === 0)
                    return element

                // Safely validate and cast element.elements
                const childElements = element.elements
                if (
                    !Array.isArray(childElements) ||
                    childElements.length === 0
                ) {
                    return element
                }

                const updatedChildElements = childElements.map(
                    (el: Partial<ChildTextContentElement>) =>
                        processResetElement(el, resetOptionType)
                )

                return {
                    ...element,
                    elements: updatedChildElements,
                }
            }
            return element
        })
        return updatedElements
    } catch (error) {
        console.error('Error resetting option group elements:', error)
        return elements
    }
}
